import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAPI, deleteMatchStat, deleteMatchSub } from '../api';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CloseIcon from '@mui/icons-material/Close';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function Match() {
  const navigate = useNavigate();
  const location = useLocation();
  const [matchData, setMatchData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [composition, setComposition] = useState({});
  const [currentMatchId, setCurrentMatchId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [startTimestamp, setStartTimestamp] = useState(null); // Timestamp de démarrage du chrono
  const [pausedTime, setPausedTime] = useState(0); // Temps accumulé avant la pause
  const [score, setScore] = useState({ home: 0, away: 0 });
  const [period, setPeriod] = useState(1);
  const [isHalftime, setIsHalftime] = useState(false);
  const [isMatchEnded, setIsMatchEnded] = useState(false);
  const [selectedPlayerDialog, setSelectedPlayerDialog] = useState({ open: false, action: '', team: '' });
  const [selectedAction, setSelectedAction] = useState('');
  const [changePlayerOutDialog, setChangePlayerOutDialog] = useState(false);
  const [changePlayerInDialog, setChangePlayerInDialog] = useState(false);
  const [selectedPlayerOut, setSelectedPlayerOut] = useState(null);
  const [selectedPlayerIn, setSelectedPlayerIn] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [matchStats, setMatchStats] = useState({ summary: { home: [], away: [] }, details: [] });
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [assistDialogOpen, setAssistDialogOpen] = useState(false);
  const [selectedScorer, setSelectedScorer] = useState(null);
  const [selectedAssist, setSelectedAssist] = useState(null);
  const [availableStats, setAvailableStats] = useState([]);
  const [matchPlayers, setMatchPlayers] = useState([]);
  const [playerStatsDialogOpen, setPlayerStatsDialogOpen] = useState(false);
  const [playerStats, setPlayerStats] = useState([]);

  // Fonction pour recharger les statistiques
  const loadMatchStats = async () => {
    try {
      const response = await fetchAPI(`/match-stats/${currentMatchId}`);

      if (response && Array.isArray(response.details)) {
        setMatchStats({
          summary: {
            home: Array.isArray(response.summary?.home) ? response.summary.home : [],
            away: Array.isArray(response.summary?.away) ? response.summary.away : [],
          },
          details: Array.isArray(response.details) ? response.details : []
        });
      } else {
        console.warn('[LOAD STATS] Réponse invalide ou details non array');
      }
    } catch (error) {
      console.error('[LOAD STATS] Erreur chargement stats:', error);
    }
  };

  // Initialiser depuis location.state uniquement au premier montage
  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - MATCH';
    
    if (location.state) {
      setMatchData(location.state.matchData);
      const statePlayers = location.state.players || [];
      setPlayers(statePlayers);
      setCurrentMatchId(location.state.matchId || null);
      
      // Extraire les IDs des joueurs sélectionnés pour le match (de location.state.players)
      if (statePlayers.length > 0) {
        const playerIds = statePlayers.map(p => Number(p.rowid));
        setMatchPlayers(playerIds);
      }
      
      // Si on arrive d'une nouvelle création de match, réinitialiser l'état SEULEMENT si aucune donnée n'existe
      if (location.state.fresh && location.state.matchId) {
        const key = `match-state-${location.state.matchId}`;
        const existingData = localStorage.getItem(key);
        
        if (!existingData) {
          // Vraiment un nouveau match, on initialise
          try {
            localStorage.removeItem(key);
          } catch {}
          setComposition(location.state.composition || {});
          setIsPlaying(false);
          setTime(0);
          setStartTimestamp(null);
          setPausedTime(0);
          setScore({ home: 0, away: 0 });
          setPeriod(1);
          setIsHalftime(false);
          setIsMatchEnded(false);
        }
      } else if (location.state.composition) {
        // Si ce n'est pas un nouveau match mais qu'on a une composition, on la set (navigation normale)
      setComposition(location.state.composition);
      }
    }
  }, [location.state]);

  // Si on arrive sans state (ex: reprise depuis l'accueil ou refresh), vérifier dans localStorage
  useEffect(() => {
    // Attendre que location.state soit traité avant de vérifier
    if (location.state) {
      return;
    }
    
    if (currentMatchId) {
      return;
    }
    
    // Chercher un match en cours dans le localStorage
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith('match-state-'));
      
      for (const key of keys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        
        if (parsed && parsed.isMatchEnded === false) {
          const id = key.replace('match-state-', '');
          setCurrentMatchId(Number(id));
          return;
        }
      }
      // Si aucun match en cours, rediriger vers l'accueil
      navigate('/');
    } catch (e) {
      navigate('/');
    }
  }, [currentMatchId, navigate, location.state]);

  // Persistance locale (localStorage) du chrono, score et composition
  const storageKey = currentMatchId ? `match-state-${currentMatchId}` : null;

  // Charger l'état sauvegardé au montage quand matchId est connu
  useEffect(() => {
    if (!currentMatchId) return;
    
    const key = `match-state-${currentMatchId}`;
    try {
      const raw = localStorage.getItem(key);
      
      if (raw) {
        const parsed = JSON.parse(raw);
        
        // Si des données existent dans localStorage, cela signifie que le match a déjà commencé
        // donc on charge les données même si fresh est true (cela peut arriver après un refresh)
        if (typeof parsed.time === 'number') {
          setTime(parsed.time);
        }
        if (typeof parsed.pausedTime === 'number') {
          setPausedTime(parsed.pausedTime);
        }
        if (typeof parsed.isPlaying === 'boolean') {
          setIsPlaying(parsed.isPlaying);
          // Si le chrono tournait, recalculer avec le nouveau timestamp
          if (parsed.isPlaying && parsed.startTimestamp) {
            const elapsed = Math.floor((Date.now() - parsed.startTimestamp) / 1000);
            setTime(parsed.pausedTime + elapsed);
            setStartTimestamp(Date.now() - (elapsed * 1000)); // Ajuster le timestamp
          }
        }
        if (parsed.score && typeof parsed.score.home === 'number' && typeof parsed.score.away === 'number') {
          setScore(parsed.score);
        }
        if (parsed.composition && typeof parsed.composition === 'object') {
          setComposition(parsed.composition);
        }
        if (parsed.period) setPeriod(parsed.period);
        if (typeof parsed.isHalftime === 'boolean') setIsHalftime(parsed.isHalftime);
        if (typeof parsed.isMatchEnded === 'boolean') setIsMatchEnded(parsed.isMatchEnded);
      }
    } catch (e) {
      // Erreur silencieuse
    }
  }, [currentMatchId]);

  // Sauvegarder à chaque changement significatif (avec délai pour éviter l'écrasement initial)
  useEffect(() => {
    if (!currentMatchId) return;
    
    const key = `match-state-${currentMatchId}`;
    const timeoutId = setTimeout(() => {
      try {
        const stateToSave = {
          time,
          isPlaying,
          startTimestamp,
          pausedTime,
          score,
          composition,
          period,
          isHalftime,
          isMatchEnded,
        };
        localStorage.setItem(key, JSON.stringify(stateToSave));
      } catch (e) {
        // Erreur silencieuse
      }
    }, 100); // Délai de 100ms pour éviter l'écrasement initial
    
    return () => clearTimeout(timeoutId);
  }, [currentMatchId, time, isPlaying, startTimestamp, pausedTime, score, composition, period, isHalftime, isMatchEnded]);


  // Charger la liste des joueurs si absente (utile après refresh/reprise)
  useEffect(() => {
    if (players.length === 0) {
      fetch(`${API_URL}/players`)
        .then(res => res.json())
        .then(data => setPlayers(Array.isArray(data) ? data : []))
        .catch(e => console.error('Erreur chargement joueurs', e));
    }
  }, [players.length]);

  // Charger les données du match si absentes (utile après refresh/reprise)
  useEffect(() => {
    if (currentMatchId && !matchData) {
      fetch(`${API_URL}/matches/${currentMatchId}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.label_adversaire) {
            setMatchData({ adversaire: data.label_adversaire });
          }
        })
        .catch(e => console.error('Erreur chargement match', e));
    }
  }, [currentMatchId, matchData]);

  // Charger les statistiques disponibles au montage
  useEffect(() => {
    fetch(`${API_URL}/statistiques`)
      .then(res => res.json())
      .then(data => {
        setAvailableStats(Array.isArray(data) ? data : []);
      })
      .catch(e => console.error('Erreur chargement statistiques', e));
  }, []);

  // Chronomètre basé sur timestamp réel (fonctionne même en arrière-plan)
  useEffect(() => {
    let interval = null;
    if (isPlaying && startTimestamp) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimestamp) / 1000);
        setTime(pausedTime + elapsed);
      }, 100); // Mise à jour toutes les 100ms pour plus de précision
    }
    return () => clearInterval(interval);
  }, [isPlaying, startTimestamp, pausedTime]);

  // Détection seuils 45' et 90' (sans bloquer le temps additionnel)
  useEffect(() => {
    const minutes = Math.floor(time / 60);
    // On n'arrête plus automatiquement le chrono à 45 minutes
    // L'utilisateur peut continuer et cliquer sur "Passer en mi-temps" quand il veut
    if (period === 2 && minutes >= 90 && !isMatchEnded) {
      // Match peut continuer (temps additionnel), on n'arrête pas le chrono, mais on peut proposer la fin
      // Ici on ne force rien; un bouton "Fin du match" sera affiché
    }
  }, [time, period, isHalftime, isMatchEnded]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (isMatchEnded) return;
    
    // Si on est en mi-temps et on clique play, on lance la 2e mi-temps
    if (isHalftime && period === 1) {
      setIsHalftime(false);
      setPeriod(2);
      setPausedTime(45 * 60); // Temps accumulé = 45 minutes
      setStartTimestamp(Date.now()); // Nouveau départ
      setIsPlaying(true);
      
      // Mettre à jour le period dans la base de données
      fetch(`${API_URL}/matches/${currentMatchId}/period`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: 2 })
      }).catch(err => console.error('Erreur mise à jour period:', err));
      
      return;
    }
    
    if (isPlaying) {
      // Pause : sauvegarder le temps actuel
      setPausedTime(time);
      setStartTimestamp(null);
      setIsPlaying(false);
    } else {
      // Play : démarrer avec le timestamp actuel
      setStartTimestamp(Date.now());
      setIsPlaying(true);
    }
  };

  // Vérifier le statut du match via l'API
  useEffect(() => {
    if (!currentMatchId) return;
    
      const checkMatchStatus = async () => {
        try {
          const response = await fetchAPI(`/matches/${currentMatchId}`);
        if (response && response.status === 'termine') {
          setIsMatchEnded(true);
          navigate('/');
        }
      } catch (e) {
        console.error('Erreur vérification statut match:', e);
      }
    };
    
    checkMatchStatus();
  }, [currentMatchId, navigate]);

  const handleStatAction = async (action, team) => {
    if (isMatchEnded) return;
    setSelectedAction(action);
    // Équipe adverse: pas de pop-up, on enregistre directement
    if (team === 'away') {
      // playerId null côté adverse
      try {
        await fetch(`${API_URL}/match-stats`, {
        method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            matchId: currentMatchId,
            playerId: null,
            action,
            minute: formatTime(time),
            team
          })
        });
        
        // Logique de corrélation automatique pour l'équipe adverse
        if (action === 'Tir Cadre') {
          // Si l'adversaire fait un tir cadré, ajouter un sauvetage pour nous
          await fetch(`${API_URL}/match-stats`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              matchId: currentMatchId,
              playerId: null, // Pas de joueur spécifique pour les sauvetages automatiques
              action: 'Sauvetage/Arrêt',
              minute: formatTime(time),
              team: 'home'
            })
          });
        } else if (action === 'Sauvetage/Arrêt') {
          // Si l'adversaire fait un sauvetage, ajouter un tir cadré pour nous
          await fetch(`${API_URL}/match-stats`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              matchId: currentMatchId,
              playerId: null, // Pas de joueur spécifique pour les tirs automatiques
              action: 'Tir Cadre',
              minute: formatTime(time),
              team: 'home'
            })
          });
        }
        
        // Recharger les stats
        await loadMatchStats();
      } catch (e) {
        console.error('Erreur enregistrement stat adverse', e);
      }
      return;
    }
    // Équipe locale: ouvrir pop-up avec terrain
    setSelectedPlayerDialog({ open: true, action, team });
  };

  const handlePlayerSelect = async (playerId) => {
    // Enregistrer la statistique dans la base de données
    try {
      const response = await fetch(`${API_URL}/match-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchId: currentMatchId,
          playerId,
          action: selectedAction,
          minute: formatTime(time),
          team: selectedPlayerDialog.team
        })
      });

      if (response.ok) {
        const text = await response.text();

        if (text) {
          JSON.parse(text);
        }

        // Logique de corrélation automatique
        if (selectedPlayerDialog.team === 'home') {
          if (selectedAction === 'Tir Cadre') {
            // Si nous faisons un tir cadré, ajouter un sauvetage pour l'adversaire
            await fetch(`${API_URL}/match-stats`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                matchId: currentMatchId,
                playerId: null, // Pas de joueur spécifique pour les sauvetages automatiques
                action: 'Sauvetage/Arrêt',
                minute: formatTime(time),
                team: 'away'
              })
            });
          } else if (selectedAction === 'Sauvetage/Arrêt') {
            // Si nous faisons un sauvetage, ajouter un tir cadré pour l'adversaire
            await fetch(`${API_URL}/match-stats`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                matchId: currentMatchId,
                playerId: null, // Pas de joueur spécifique pour les tirs automatiques
                action: 'Tir Cadre',
                minute: formatTime(time),
                team: 'away'
              })
            });
          }
        }

        // Recharger les stats
        await loadMatchStats();
      } else {
        const errorText = await response.text();
        console.error('Erreur enregistrement stat:', errorText);
      }
    } catch (e) {
      console.error('[STAT] Erreur enregistrement stat:', e);
    }
    setSelectedPlayerDialog({ open: false, action: '', team: '' });
    setSelectedAction('');
  };

  const handleGoal = async (team) => {
    if (isMatchEnded) return;
    if (team === 'home') {
      setSelectedScorer(null);
      setSelectedAssist(null);
      setGoalDialogOpen(true);
    } else {
      setScore(prev => ({ ...prev, away: prev.away + 1 }));
      
      try {
        // Enregistrer le but contre dans la base de données
        await fetch(`${API_URL}/match-stats`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            matchId: currentMatchId,
            playerId: null, // Pas de joueur spécifique pour les buts contre
            action: 'BUT',
            minute: formatTime(time),
            team: 'away'
          })
        });

        // Enregistrer automatiquement un tir cadré pour l'équipe adverse
        await fetch(`${API_URL}/match-stats`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            matchId: currentMatchId,
            playerId: null,
            action: 'Tir Cadre',
            minute: formatTime(time),
            team: 'away'
          })
        });

        // Recharger les statistiques
        await loadMatchStats();
      } catch (error) {
        console.error('Erreur enregistrement but contre:', error);
      }
    }
  };

  const handleScorerSelect = (playerId) => {
    setSelectedScorer(playerId);
    setGoalDialogOpen(false);
    setAssistDialogOpen(true);
  };

  const handleAssistConfirm = async () => {
    try {
      // Enregistrer le but avec le buteur
      await fetch(`${API_URL}/match-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchId: currentMatchId,
          playerId: selectedScorer,
          action: 'BUT',
          minute: formatTime(time),
          team: 'home'
        })
      });

      // Enregistrer automatiquement un tir cadré pour le buteur
      await fetch(`${API_URL}/match-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchId: currentMatchId,
          playerId: selectedScorer,
          action: 'Tir Cadre',
          minute: formatTime(time),
          team: 'home'
        })
      });

      // Enregistrer la passe décisive si sélectionnée
      if (selectedAssist && selectedAssist !== 'none') {
        await fetch(`${API_URL}/match-stats`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            matchId: currentMatchId,
            playerId: selectedAssist,
            action: 'PASSE DECISIVE',
            minute: formatTime(time),
            team: 'home'
          })
        });
      }

      setScore(prev => ({ ...prev, home: prev.home + 1 }));
      
      // Recharger les statistiques
      await loadMatchStats();
    } catch (e) {
      console.error('Erreur enregistrement but/tir:', e);
    }
    
    setAssistDialogOpen(false);
    setSelectedScorer(null);
    setSelectedAssist(null);
  };

  const handleChangeStart = () => {
    if (isMatchEnded) return;
    setSelectedPlayerOut(null);
    setSelectedPlayerIn(null);
    setChangePlayerOutDialog(true);
  };

  const handlePlayerOutSelect = (playerId) => {
    setSelectedPlayerOut(playerId);
    setChangePlayerOutDialog(false);
    setChangePlayerInDialog(true);
  };

  const handlePlayerInConfirm = () => {
    if (!selectedPlayerIn || !selectedPlayerOut) {
      alert('Veuillez sélectionner un remplaçant');
      return;
    }

    // Mettre à jour la composition (remplacer playerOut par playerIn)
    const newComposition = { ...composition };
    const posKey = Object.keys(newComposition).find((k) => Number(newComposition[k]) === Number(selectedPlayerOut));
    if (posKey) {
      newComposition[posKey] = selectedPlayerIn;
      setComposition(newComposition);
    }

    // Enregistrer le changement
    fetch(`${API_URL}/match-subs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        matchId: currentMatchId,
        playerOut: selectedPlayerOut,
        playerIn: selectedPlayerIn,
        minute: formatTime(time)
      })
      }).then(() => {
        // Recharger les statistiques
        fetchAPI(`/match-stats/${currentMatchId}`).then(response => {
          if (response && Array.isArray(response.details)) {
            setMatchStats(response);
          }
        });
      }).catch((e) => console.error('Erreur enregistrement changement', e));

    // Fermer les dialogs et réinitialiser
    setChangePlayerInDialog(false);
    setSelectedPlayerOut(null);
    setSelectedPlayerIn(null);
  };

  const calculatePlayerStats = () => {
    try {
      if (!players || players.length === 0 || !composition) {
        return [];
      }

      const playerMap = new Map();
      
      // Initialiser tous les joueurs du match
      const titulaires = getTitulaires();
      titulaires.forEach(p => {
        playerMap.set(p.rowid, {
          id: p.rowid,
          name: `${p.firstname} ${p.lastname}`,
          position: p.label,
          minutesPlayed: 0,
          stats: {}
        });
      });
      
      const bench = getBench();
      bench.forEach(p => {
        if (!playerMap.has(p.rowid)) {
          playerMap.set(p.rowid, {
            id: p.rowid,
            name: `${p.firstname} ${p.lastname}`,
            position: p.label,
            minutesPlayed: 0,
            stats: {}
          });
        }
      });

      // Calculer le temps de jeu et les statistiques
      if (matchStats && matchStats.details && Array.isArray(matchStats.details)) {
        matchStats.details.forEach(stat => {
          if (stat.playerId && playerMap.has(stat.playerId)) {
            const player = playerMap.get(stat.playerId);
            
            // Compter les statistiques
            if (!player.stats[stat.label]) {
              player.stats[stat.label] = 0;
            }
            player.stats[stat.label]++;
          }
        });
      }

      // Calculer les minutes jouées (simplification : titulaires = temps total, remplaçants = à calculer)
      const compIds = Object.values(composition).map(v => Number(v));
      playerMap.forEach((player, id) => {
        if (compIds.includes(id)) {
          // Joueur titulaire : temps total du match
          player.minutesPlayed = Math.floor(time / 60);
        }
      });

      return Array.from(playerMap.values());
    } catch (error) {
      console.error('Erreur dans calculatePlayerStats:', error);
      return [];
    }
  };

  const handleDeleteStat = async (statId, isSub = false) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette statistique ?')) {
      return;
    }

    try {
      // Récupérer les détails de la statistique avant suppression pour vérifier si c'est un but
      const currentStats = matchStats?.details || [];
      const statToDelete = currentStats.find(stat => stat.id === statId);
      
      if (isSub) {
        // Pour les changements, inverser la substitution dans la composition
        // Le playerName est au format "Joueur Sorti -> Joueur Entré"
        if (statToDelete && statToDelete.playerName) {
          const [playerOutName, playerInName] = statToDelete.playerName.split(' -> ').map(s => s.trim());
          
          // Trouver les IDs des joueurs
          const playerOut = players.find(p => `${p.firstname} ${p.lastname}` === playerOutName);
          const playerIn = players.find(p => `${p.firstname} ${p.lastname}` === playerInName);
          
          if (playerOut && playerIn) {
            // Inverser le changement dans la composition
            const newComposition = { ...composition };
            const posKey = Object.keys(newComposition).find((k) => Number(newComposition[k]) === Number(playerIn.id_player));
            if (posKey) {
              newComposition[posKey] = playerOut.id_player;
              setComposition(newComposition);
            }
          }
        }
        
        // Pour les changements, l'ID a été augmenté de 1000000 côté backend, il faut le soustraire
        const realSubId = statId - 1000000;
        await deleteMatchSub(realSubId);
      } else {
        await deleteMatchStat(statId);
        
        // Si c'était un but, supprimer aussi les stats associées (passe décisive et tir cadré)
        if (statToDelete && statToDelete.label === 'BUT') {
          const playerId = statToDelete.playerId;
          const minute = statToDelete.minute;
          const team = statToDelete.team;
          
          // Trouver et supprimer le tir cadré associé (même joueur, même minute, même équipe)
          const tirCadreToDelete = currentStats.find(stat => 
            stat.label === 'Tir Cadre' && 
            stat.playerId === playerId && 
            stat.minute === minute &&
            stat.team === team
          );
          if (tirCadreToDelete) {
            await deleteMatchStat(tirCadreToDelete.id);
          }
          
          // Trouver et supprimer la passe décisive associée (même minute, même équipe)
          const passeToDelete = currentStats.find(stat => 
            stat.label === 'PASSE DECISIVE' && 
            stat.minute === minute &&
            stat.team === team
          );
          if (passeToDelete) {
            await deleteMatchStat(passeToDelete.id);
          }
        }
      }
      
      // Si c'était un but, mettre à jour le score
      if (statToDelete && statToDelete.label === 'BUT') {
        if (statToDelete.team === 'home') {
          setScore(prev => ({ ...prev, home: Math.max(0, prev.home - 1) }));
        } else if (statToDelete.team === 'away') {
          setScore(prev => ({ ...prev, away: Math.max(0, prev.away - 1) }));
        }
      }
        
      // Recharger les statistiques
      const response = await fetchAPI(`/match-stats/${currentMatchId}`);
      if (response && Array.isArray(response.details)) {
        setMatchStats(response);
      }
    } catch (error) {
      console.error('Erreur suppression statistique:', error);
      alert('Erreur lors de la suppression de la statistique');
    }
  };

  const getTitulaires = () => {
    if (!composition || !players || players.length === 0) {
      return [];
    }
    const compIds = Object.values(composition).map((v) => Number(v));
    return players.filter(player => compIds.includes(Number(player.rowid)));
  };

  const getBench = () => {
    if (!composition || !players || players.length === 0) {
      return [];
    }
    const compIds = Object.values(composition).map((v) => Number(v));
    
    // Filtrer par les joueurs sélectionnés pour le match (matchPlayers)
    // Si matchPlayers est vide, utiliser tous les joueurs (fallback)
    const availablePlayers = matchPlayers.length > 0
      ? players.filter(p => matchPlayers.includes(Number(p.rowid)))
      : players;
    
    // Les remplaçants sont les joueurs sélectionnés pour le match qui ne sont pas titulaires
    const bench = availablePlayers.filter(player => !compIds.includes(Number(player.rowid)));
    return bench;
  };

  // Générer des positions à partir de la composition en utilisant le poste de la composition (clé), pas le poste par défaut du joueur
  const generatePositionsFromComposition = () => {
    // Vérifications de sécurité
    if (!composition || !players || players.length === 0) {
      return [];
    }

    const normalizeFromPosId = (posId) => {
      const v = (posId || '').toLowerCase();
      if (v.startsWith('gardien')) return 'gardien';
      if (v.startsWith('défenseur') || v.startsWith('defenseur') || v.startsWith('def')) return 'defenseur';
      if (v.startsWith('milieu') || v.startsWith('mil')) return 'milieu';
      if (v.startsWith('attaquant') || v.startsWith('att')) return 'attaquant';
      return 'autre';
    };

    const posteToPlayers = {};
    Object.entries(composition || {}).forEach(([posId, playerId]) => {
      const player = players.find(p => Number(p.rowid) === Number(playerId));
      if (!player) return;
      const cat = normalizeFromPosId(posId);
      if (!posteToPlayers[cat]) posteToPlayers[cat] = [];
      posteToPlayers[cat].push({ posId, ...player });
    });

    const positions = [];
    const pushWithCoords = (cat, indexInLine, count, p) => {
      let x = 50; let y = 50;
      switch (cat) {
        case 'attaquant':
          if (count === 1) { 
            x = 50; 
            y = 10; 
          } else if (count === 2) { 
            x = 30 + (indexInLine * 40); 
            y = 10; 
          } else if (count === 3) { 
            if (indexInLine === 1) {
              x = 50;
              y = 5;
            } else {
              x = 20 + (indexInLine * 30);
              y = 15;
            }
          } else if (count === 4) { 
            if (indexInLine < 2) {
              x = 15 + (indexInLine * 70);
              y = 5;
            } else {
              x = 30 + ((indexInLine - 2) * 40);
              y = 15;
            }
          } else { 
            x = 50; 
            y = 10; 
          }
          break;
        case 'milieu':
          if (count === 1) { 
            x = 50; 
            y = 35; 
          } else if (count === 2) { 
            x = 30 + (indexInLine * 40); 
            y = 35; 
          } else if (count === 3) { 
            if (indexInLine === 1) {
              x = 50;
              y = 40;
            } else {
              x = 20 + (indexInLine * 30);
              y = 30;
            }
          } else if (count === 4) { 
            if (indexInLine < 2) {
              x = 15 + (indexInLine * 70);
              y = 30;
            } else {
              x = 30 + ((indexInLine - 2) * 40);
              y = 40;
            }
          } else if (count === 5) { 
            if (indexInLine < 3) {
              x = 15 + (indexInLine * 35);
              y = 30;
            } else {
              x = 30 + ((indexInLine - 3) * 40);
              y = 45;
            }
          } else { 
            x = 50; 
            y = 35; 
          }
          break;
        case 'defenseur':
          if (count === 1) { 
            x = 50; 
            y = 65; 
          } else if (count === 2) { 
            x = 30 + (indexInLine * 40); 
            y = 65; 
          } else if (count === 3) { 
            if (indexInLine === 1) {
              x = 50;
              y = 70;
            } else {
              x = 20 + (indexInLine * 30);
              y = 60;
            }
          } else if (count === 4) { 
            if (indexInLine < 2) {
              x = 15 + (indexInLine * 70);
              y = 60;
            } else {
              x = 30 + ((indexInLine - 2) * 40);
              y = 70;
            }
          } else if (count === 5) { 
            if (indexInLine < 2) {
              x = 15 + (indexInLine * 70);
              y = 60;
            } else {
              x = 20 + ((indexInLine - 2) * 30);
              y = 70;
            }
          } else { 
            x = 50; 
            y = 65; 
          }
          break;
        case 'gardien':
          x = 50; 
          y = 85; 
          break;
        default:
          x = 50; 
          y = 50;
      }
      positions.push({ id: p.posId || p.rowid, x, y, p });
    };

    Object.entries(posteToPlayers).forEach(([cat, arr]) => {
      arr.forEach((player, i) => pushWithCoords(cat, i, arr.length, player));
    });

    return positions;
  };

  return (
    <div>
      
      <Box sx={{ maxWidth: 360, mx: 'auto', p: 1.5, bgcolor: '#f5f5f5' }}>

        {/* Statut du match */}
        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            {period === 1 ? '1ère mi-temps' : '2ème mi-temps'}
          </Typography>
          
          {/* Timer */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, gap: 1 }}>
            <Typography variant="h6" sx={{ mr: 1, fontFamily: 'monospace', minWidth: '64px', textAlign: 'right' }}>
              {isHalftime ? '45:00' : formatTime(time)}
            </Typography>
            <IconButton size="small" onClick={handlePlayPause} sx={{ bgcolor: '#1976d2', color: 'white', width: 32, height: 32 }}>
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Box>
          {/* Boutons contextuels pour mi-temps / fin de match */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {period === 1 && Math.floor(time / 60) >= 45 && !isHalftime && (
              <Button size="small" variant="outlined" onClick={() => {
                setIsPlaying(false); // Arrêter le chronomètre
                setIsHalftime(true); // Passer en mi-temps
                // On ne change pas le temps ici, il sera affiché à 45:00 visuellement
              }}>
                Passer en mi-temps
              </Button>
            )}
            {period === 2 && Math.floor(time / 60) >= 2 && !isMatchEnded && (
              <Button size="small" variant="outlined" color="error" onClick={async () => { 
                if (window.confirm('Êtes-vous sûr de vouloir terminer le match ?')) {
                  setIsPlaying(false); 
                  setIsMatchEnded(true);
                  
                  // Mettre à jour le statut dans la base de données
                  try {
                    await fetchAPI(`/matches/${currentMatchId}/status`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ status: 'termine' })
                    });
                  } catch (e) {
                    console.error('Erreur mise à jour statut match:', e);
                  }
                  
                  // Nettoyer le localStorage pour ce match
                  try {
                    const key = `match-state-${currentMatchId}`;
                    localStorage.removeItem(key);
                    
                    // Déclencher un événement de stockage pour notifier les autres onglets
                    window.dispatchEvent(new StorageEvent('storage', {
                      key: key,
                      newValue: null,
                      oldValue: localStorage.getItem(key)
                    }));
                  } catch (e) {
                    console.error('Erreur nettoyage localStorage:', e);
                  }
                  
                  // Redirection immédiate vers l'accueil
                  navigate('/');
                }
              }}>
                Fin du match
              </Button>
            )}
          </Box>

          {/* Score */}
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            ESMA {score.home} - {score.away} {matchData?.adversaire || 'Adversaire'}
          </Typography>
        </Box>

        {/* Buts rapides */}
        {!isMatchEnded && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleGoal('home')}
            sx={{ 
              bgcolor: '#4caf50',
              '&:hover': { bgcolor: '#45a049' },
                py: 0.75,
                fontSize: '0.95rem'
            }}
          >
            <SportsSoccerIcon sx={{ mr: 1 }} />
            BUT POUR
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleGoal('away')}
            sx={{ 
              bgcolor: '#f44336',
              '&:hover': { bgcolor: '#da190b' },
                py: 0.75,
                fontSize: '0.95rem'
            }}
          >
            <SportsSoccerIcon sx={{ mr: 1 }} />
            BUT CONTRE
          </Button>
        </Box>
        )}

        {/* Section Pour */}
        {!isMatchEnded && !isHalftime && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Pour :
          </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
              {availableStats
                .filter(stat => stat.is_for_us === 1 && !['But Pour', 'Changement', 'BUT'].includes(stat.label))
                .map((stat) => (
                  <Button 
                    key={stat.rowid}
                    size="small"
                    variant="outlined" 
                    onClick={() => handleStatAction(stat.label, 'home')}
                    sx={stat.label.includes('Carton') ? { color: stat.label.includes('Rouge') ? '#f44336' : '#ff9800' } : {}}
                  >
                    {stat.label}
            </Button>
                ))}
          </Box>
        </Box>
        )}

        {/* Section Contre */}
        {!isMatchEnded && !isHalftime && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Contre :
          </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
              {availableStats
                .filter(stat => stat.is_for_us === 0 && !['But Contre', 'BUT', 'Tir Cadre'].includes(stat.label))
                .map((stat) => (
                  <Button 
                    key={stat.rowid}
                    size="small"
                    variant="outlined" 
                    onClick={() => handleStatAction(stat.label, 'away')}
                    sx={stat.label.includes('Carton') ? { color: stat.label.includes('Rouge') ? '#f44336' : '#ff9800' } : {}}
                  >
                    {stat.label}
            </Button>
                ))}
          </Box>
        </Box>
        )}

        {/* Changement */}
        {!isMatchEnded && (
        <Button
          variant="contained"
          fullWidth
          sx={{ 
            bgcolor: '#00000',
            '&:hover': { bgcolor: '#7b1fa2' },
              py: 0.9,
              fontSize: '1rem'
          }}
            onClick={handleChangeStart}
        >
          CHANGEMENT
        </Button>
        )}

        {/* Bouton afficher les statistiques */}
        <Button
          variant="outlined"
          fullWidth
          sx={{ mt: 1 }}
          onClick={async () => {
            setStatsDialogOpen(true);
            try {
              const res = await fetch(`${API_URL}/match-stats/${currentMatchId}`);
              const data = await res.json();
              if (data && data.summary && data.details) {
                setMatchStats({
                  summary: {
                    home: Array.isArray(data.summary.home) ? data.summary.home : [],
                    away: Array.isArray(data.summary.away) ? data.summary.away : [],
                  },
                  details: Array.isArray(data.details) ? data.details : []
                });
              } else {
                setMatchStats({ summary: { home: [], away: [] }, details: [] });
              }
            } catch (e) {
              console.error('Erreur chargement stats match', e);
              setMatchStats({ summary: { home: [], away: [] }, details: [] });
            }
          }}
        >
          AFFICHER LES STATISTIQUES
        </Button>

        {/* Bouton Statistiques par joueur */}
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            loadMatchStats(); // Charger les stats avant d'ouvrir le dialog
            setPlayerStatsDialogOpen(true);
          }}
          sx={{ 
            bgcolor: '#4caf50',
            '&:hover': { bgcolor: '#388e3c' },
            py: 0.9,
            fontSize: '1rem',
            mt: 1
          }}
        >
          STATISTIQUES PAR JOUEUR
        </Button>

        {/* Bouton Annuler le match */}
        {!isMatchEnded && (
          <Button
            variant="contained"
            fullWidth
            onClick={async () => {
              if (window.confirm('Êtes-vous sûr de vouloir ANNULER ce match ? Toutes les données seront supprimées définitivement.')) {
                try {
                  // Supprimer le match de la base de données
                  await fetch(`${API_URL}/matches/${currentMatchId}`, {
                    method: 'DELETE'
                  });
                  
                  // Nettoyer le localStorage
                  const key = `match-state-${currentMatchId}`;
                  localStorage.removeItem(key);
                  
                  // Rediriger vers l'accueil
                  navigate('/');
                } catch (error) {
                  console.error('Erreur annulation match:', error);
                  alert('Erreur lors de l\'annulation du match');
                }
              }
            }}
            sx={{ 
              bgcolor: '#d32f2f',
              '&:hover': { bgcolor: '#b71c1c' },
              py: 0.9,
              fontSize: '1rem',
              mt: 1
            }}
          >
            ANNULER LE MATCH
          </Button>
        )}

        {/* Dialog stat - terrain pour sélectionner un joueur (équipe locale) */}
        <Dialog 
          open={selectedPlayerDialog.open} 
          onClose={() => setSelectedPlayerDialog({ open: false, action: '', team: '' })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Sélectionner un joueur - {selectedAction}
          </DialogTitle>
          <DialogContent>
            {/* Terrain */}
              <Box 
              sx={{ 
                position: 'relative',
                width: '100%',
                  height: '460px',
                backgroundColor: '#2e7d32',
                borderRadius: 2,
                border: '3px solid #fff',
                mb: 2,
                overflow: 'hidden'
              }}
            >
              {/* Lignes terrain */}
              <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '3px', backgroundColor: '#fff', transform: 'translateY(-50%)' }} />
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '22px', height: '22px', backgroundColor: '#fff', borderRadius: '50%', transform: 'translate(-50%, -50%)' }} />

              {generatePositionsFromComposition().map((pos) => (
                <Box
                  key={pos.p?.rowid}
                  sx={{
                    position: 'absolute',
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => handlePlayerSelect(pos.p?.rowid)}
                    sx={{
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.7rem',
                      borderRadius: 1
                    }}
                  >
                    {pos.p?.firstname}. {pos.p?.lastname?.charAt(0)}
                  </Button>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedPlayerDialog({ open: false, action: '', team: '' })}>
              Annuler
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: liste des statistiques du match */}
        <Dialog 
          open={statsDialogOpen}
          onClose={() => setStatsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          </DialogTitle>
          <DialogContent sx={{ maxHeight: 480, overflowY: 'auto' }}>
            {/* Comparatif visuel (barres opposées) */}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold', color: '#111' }}>Comparatif</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
              {(() => {
                const exclude = new Set(['BUT', 'PASSE DECISIVE']);
                const homeArr = (matchStats.summary.home || []).filter(r => !exclude.has(r.label));
                const awayArr = (matchStats.summary.away || []).filter(r => !exclude.has(r.label));
                const homeMap = Object.fromEntries(homeArr.map(r => [r.label, Number(r.count) || 0]));
                const awayMap = Object.fromEntries(awayArr.map(r => [r.label, Number(r.count) || 0]));
                const allLabels = Array.from(new Set([...homeArr.map(r=>r.label), ...awayArr.map(r=>r.label)]));
                return allLabels.map((label) => {
                  const left = homeMap[label] || 0;
                  const right = awayMap[label] || 0;
                  const total = Math.max(1, left + right);
                  const leftPct = Math.round((left / total) * 100);
                  const rightPct = 100 - leftPct;
                  return (
                    <Box key={`cmp-${label}`} sx={{ color: '#111' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111' }}>{left}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111' }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111' }}>{right}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', width: '100%', height: 10, borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${leftPct}%`, backgroundColor: '#64b5f6' }} />
                        <Box sx={{ width: `${rightPct}%`, backgroundColor: '#d32f2f' }} />
                      </Box>
                    </Box>
                  );
                });
              })()}
            </Box>

            {/* Détails */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold' }}>Détails</Typography>
            <List dense>
              {matchStats.details.length === 0 && (
                <ListItem><ListItemText primary="Aucune statistique enregistrée" /></ListItem>
              )}
              {[...matchStats.details].sort((a, b) => {
                // plus récent en haut: convertir minute en secondes pour tri numérique
                const timeToSeconds = (timeStr) => {
                  const [mins, secs] = timeStr.split(':').map(Number);
                  return mins * 60 + secs;
                };
                const aSeconds = timeToSeconds(a.minute);
                const bSeconds = timeToSeconds(b.minute);
                if (bSeconds === aSeconds) return b.id - a.id;
                return bSeconds - aSeconds;
              }).map((s) => (
                <ListItem 
                  key={s.id}
                  sx={{ 
                    position: 'relative',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                    <ListItemText 
                    primary={`${s.minute} - ${s.label} ${s.team === 'home' ? '(ESMA)' : '(Adverse)'}`}
                    secondary={s.team === 'home' ? s.playerName : ''}
                    sx={{ pr: 4 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteStat(s.id, s.label === 'Changement')}
                    sx={{ 
                      position: 'absolute',
                      top: 0,
                      right: -100,
                      color: 'error.main',
                      '&:hover': { backgroundColor: 'error.light', color: 'white' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatsDialogOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour but - terrain pour sélectionner le buteur */}
        <Dialog
          open={goalDialogOpen}
          onClose={() => setGoalDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>But pour ESMA - Sélectionner le buteur</DialogTitle>
          <DialogContent>
            {/* Terrain */}
            <Box 
              sx={{ 
                position: 'relative',
                width: '100%',
                height: '460px',
                backgroundColor: '#2e7d32',
                borderRadius: 2,
                border: '3px solid #fff',
                mb: 2,
                overflow: 'hidden'
              }}
            >
              {/* Lignes terrain */}
              <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '3px', backgroundColor: '#fff', transform: 'translateY(-50%)' }} />
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '22px', height: '22px', backgroundColor: '#fff', borderRadius: '50%', transform: 'translate(-50%, -50%)' }} />

              {generatePositionsFromComposition().map((pos) => (
                <Box
                  key={pos.p?.rowid}
                  sx={{
                    position: 'absolute',
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => handleScorerSelect(pos.p?.rowid)}
                    sx={{
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.7rem',
                      borderRadius: 1
                    }}
                  >
                    {pos.p?.firstname}. {pos.p?.lastname?.charAt(0)}
                  </Button>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGoalDialogOpen(false)}>Annuler</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour passeur décisif */}
        <Dialog
          open={assistDialogOpen}
          onClose={() => setAssistDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Passeur décisif (optionnel)</DialogTitle>
          <DialogContent>
            <List dense>
              <ListItem disablePadding>
                <ListItemButton 
                  selected={selectedAssist === 'none'} 
                  onClick={() => setSelectedAssist('none')}
                >
                  <ListItemText primary="Aucun" />
                </ListItemButton>
              </ListItem>
              {getTitulaires().map((p) => (
                <ListItem key={`assist-${p.rowid}`} disablePadding>
                  <ListItemButton 
                    selected={selectedAssist === p.rowid} 
                    onClick={() => setSelectedAssist(p.rowid)}
                  >
                    <ListItemText primary={`${p.firstname} ${p.lastname}`} secondary={p.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssistDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAssistConfirm}>
              Valider le but
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour changement - terrain pour sélectionner le joueur qui sort */}
        <Dialog
          open={changePlayerOutDialog}
          onClose={() => setChangePlayerOutDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Changement - Sélectionner le joueur qui sort</DialogTitle>
          <DialogContent>
            {/* Terrain */}
            <Box 
              sx={{ 
                position: 'relative',
                width: '100%',
                height: '460px',
                backgroundColor: '#2e7d32',
                borderRadius: 2,
                border: '3px solid #fff',
                mb: 2,
                overflow: 'hidden'
              }}
            >
              {/* Lignes terrain */}
              <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '3px', backgroundColor: '#fff', transform: 'translateY(-50%)' }} />
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '22px', height: '22px', backgroundColor: '#fff', borderRadius: '50%', transform: 'translate(-50%, -50%)' }} />

              {generatePositionsFromComposition().map((pos) => (
                <Box
                  key={pos.p?.rowid}
                  sx={{
                    position: 'absolute',
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => handlePlayerOutSelect(pos.p?.rowid)}
                    sx={{
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.7rem',
                      borderRadius: 1
                    }}
                  >
                    {pos.p?.firstname}. {pos.p?.lastname?.charAt(0)}
                  </Button>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePlayerOutDialog(false)}>Annuler</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour sélectionner le remplaçant */}
        <Dialog
          open={changePlayerInDialog}
          onClose={() => setChangePlayerInDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Remplaçant</DialogTitle>
          <DialogContent>
            <List dense>
              {getBench().map((p) => (
                <ListItem key={`change-in-${p.rowid}`} disablePadding>
                  <ListItemButton 
                    selected={selectedPlayerIn === p.rowid} 
                    onClick={() => setSelectedPlayerIn(p.rowid)}
                  >
                    <ListItemText primary={`${p.firstname} ${p.lastname}`} secondary={p.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePlayerInDialog(false)}>Annuler</Button>
            <Button onClick={handlePlayerInConfirm}>
              Valider le changement
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Statistiques par joueur */}
        <Dialog
          open={playerStatsDialogOpen}
          onClose={() => setPlayerStatsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Statistiques par joueur</DialogTitle>
          <DialogContent>
            <List>
              {calculatePlayerStats().map((player) => (
                <ListItem key={player.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee', py: 2 }}>
                  <Box sx={{ width: '100%', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {player.name}
                    </Typography>
                  </Box>
                  
                  {Object.keys(player.stats).length > 0 ? (
                    <Box sx={{ width: '100%', pl: 2 }}>
                      {Object.entries(player.stats).map(([statLabel, count]) => (
                        <Typography key={statLabel} variant="body2" sx={{ py: 0.3 }}>
                          • {statLabel}: <strong>{count}</strong>
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, fontStyle: 'italic' }}>
                      Aucune statistique enregistrée
                    </Typography>
                  )}
                </ListItem>
              ))}
              
              {calculatePlayerStats().length === 0 && (
                <ListItem>
                  <ListItemText 
                    primary="Aucune statistique disponible" 
                    secondary="Les statistiques apparaîtront une fois le match commencé"
                  />
                </ListItem>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlayerStatsDialogOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
}
